import {
  createContext,
  useCallback,
  useContext,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type ComponentProps,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { cx } from '@parrot/utils';
import { Button } from '../Button';
import { useAnchoredPosition } from '../internal/anchor';
import styles from './Menu.module.css';

const MenuContext = createContext<string | null>(null);

const useMenuId = (component: string) => {
  const id = useContext(MenuContext);
  if (id === null) throw new Error(`<${component}> must be rendered inside <Menu>.`);
  return id;
};

/**
 * A list of actions hanging off a trigger — row overflow, a user menu, anything with a
 * "more" affordance.
 *
 * The list is a popover, which is where most of this comes from: the top layer (so it
 * can't be clipped by a table's overflow or lose a z-index fight), light dismiss, and
 * Escape. The reference hand-rolled all three, five times over — KebabMenu, UserMenu,
 * HelpMenu, NotificationPanel and Select each had their own copy.
 *
 * Unlike the other composites this one is uncontrolled. The rule exists so a parent can
 * refuse a change, and there's nothing to refuse about opening a menu — the popover is
 * the single source of truth for whether it's showing.
 */
export const Menu = ({ children }: { children: ReactNode }) => {
  const id = useId();
  return <MenuContext.Provider value={id}>{children}</MenuContext.Provider>;
};

type MenuButtonProps = Omit<ComponentProps<typeof Button>, 'popoverTarget'>;

export const MenuButton = (props: MenuButtonProps) => {
  const id = useMenuId('MenuButton');
  return <Button popoverTarget={id} {...props} />;
};

type MenuListProps = Omit<HTMLAttributes<HTMLDivElement>, 'popover'> & {
  /** Which end of the trigger the list lines up with. */
  align?: 'start' | 'end';
};

export const MenuList = ({ align = 'end', className, ...divProps }: MenuListProps) => {
  const id = useMenuId('MenuList');
  const listRef = useRef<HTMLDivElement>(null);

  /* The trigger identifies itself by pointing at us, so there's no ref to thread through
     context. useId can emit colons, hence the escape. Every item points at us too, so the
     trigger is whichever one sits outside the list — the parts can come in any order. */
  const getAnchor = useCallback(
    () =>
      Array.from(document.querySelectorAll(`[popovertarget="${CSS.escape(id)}"]`)).find(
        (element) => listRef.current?.contains(element) !== true,
      ) ?? null,
    [id],
  );

  useAnchoredPosition({ elementRef: listRef, getAnchor, side: 'bottom', align });

  return (
    <div
      ref={listRef}
      id={id}
      popover={'auto'}
      className={cx(styles.list, className)}
      {...divProps}
    />
  );
};

type MenuItemProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'popoverTarget' | 'popoverTargetAction' | 'type'
> & {
  /** danger for the destructive one — deleting, revoking, disconnecting. */
  tone?: 'danger';
  onSelect?: () => void;
};

export const MenuItem = ({ tone, onSelect, className, onClick, ...buttonProps }: MenuItemProps) => {
  const id = useMenuId('MenuItem');

  return (
    <button
      type={'button'}
      // Closes the menu itself. The reference called setOpen(false) by hand at the top
      // of every single onSelect.
      popoverTarget={id}
      popoverTargetAction={'hide'}
      className={cx(styles.item, tone !== undefined && styles[tone], className)}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) onSelect?.();
      }}
      {...buttonProps}
    />
  );
};
